import React, { useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { T, useLang } from '../lib/lang';
import {
  AgencyVisual,
  ComplexityScaleVisual,
  ContextVisual,
  DogfoodingVisual,
  EcosystemVisual,
  ExoskeletonVisual,
  IFSVisual,
  PrompterVisual,
  StackVisual,
  WaveVisual,
} from './metaphors/VisualMetaphors';

type MetaphorItem = {
  id: string;
  label: { ru: string; en: string };
  title: { ru: string; en: string };
  desc: { ru: React.ReactNode; en: React.ReactNode };
  Visual: React.FC<{ isActive: boolean }>;
};

function asVisual(Component: React.FC<any>): React.FC<{ isActive: boolean }> {
  // eslint-disable-next-line react/display-name
  return ({ isActive }) => <Component isActive={isActive} />;
}

export function Metaphors() {
  const { lang } = useLang();

  const items: MetaphorItem[] = useMemo(
    () => [
      {
        id: 'context',
        label: { ru: 'metaphor 01', en: 'metaphor 01' },
        title: { ru: 'контекст как 15MB файл', en: 'context as a 15MB file' },
        desc: {
          ru: (
            <>
              не «промпт с нуля», а <span className="text-text">личная база</span> и <span className="text-text">память</span>.
              LLM начинает отвечать «изнутри твоего мира», а не из усреднённой энциклопедии.
            </>
          ),
          en: (
            <>
              not “prompting from scratch”, but <span className="text-text">personal base</span> and{' '}
              <span className="text-text">memory</span>. the model replies from your world, not from an averaged encyclopedia.
            </>
          ),
        },
        Visual: asVisual(ContextVisual),
      },
      {
        id: 'exoskeleton',
        label: { ru: 'metaphor 02', en: 'metaphor 02' },
        title: { ru: 'AI как экзоскелет', en: 'AI as an exoskeleton' },
        desc: {
          ru: (
            <>
              «тяжёлая броня» заменяет движение. «лёгкий экзо» усиливает то, что уже есть: внимание, ясность, контакт.
            </>
          ),
          en: (
            <>
              “heavy armor” replaces movement. “light exo” amplifies what’s already there: attention, clarity, contact.
            </>
          ),
        },
        Visual: ExoskeletonVisual,
      },
      {
        id: 'prompter',
        label: { ru: 'metaphor 03', en: 'metaphor 03' },
        title: { ru: 'AI как суфлёр', en: 'AI as a prompter' },
        desc: {
          ru: (
            <>
              нейросеть не выходит на сцену. она <span className="text-text">держит контур</span>, напоминает, подсвечивает, но не
              подменяет человека.
            </>
          ),
          en: (
            <>
              the model doesn’t walk on stage. it <span className="text-text">holds the frame</span>, nudges, highlights — but
              doesn’t replace a human.
            </>
          ),
        },
        Visual: asVisual(PrompterVisual),
      },
      {
        id: 'ifs',
        label: { ru: 'metaphor 04', en: 'metaphor 04' },
        title: { ru: 'IFS как система частей', en: 'IFS as a system of parts' },
        desc: {
          ru: (
            <>
              внутри не один «я», а множество ролей и защит. цель не «убрать часть», а <span className="text-text">вернуть Self</span>{' '}
              как центр.
            </>
          ),
          en: (
            <>
              inside, there isn’t one “me” but many roles and protectors. the goal isn’t to remove a part, but to restore{' '}
              <span className="text-text">Self</span> as the center.
            </>
          ),
        },
        Visual: asVisual(IFSVisual),
      },
      {
        id: 'complexity',
        label: { ru: 'metaphor 05', en: 'metaphor 05' },
        title: { ru: 'шкала сложности', en: 'complexity scale' },
        desc: {
          ru: (
            <>
              где-то между «слишком просто» и «слишком сложно» есть золотая зона — <span className="text-text">практика</span>, а не
              техно-спектакль.
            </>
          ),
          en: (
            <>
              between “too simple” and “too complex” there’s a golden zone — <span className="text-text">practice</span>, not a
              techno-show.
            </>
          ),
        },
        Visual: asVisual(ComplexityScaleVisual),
      },
      {
        id: 'timing',
        label: { ru: 'metaphor 06', en: 'metaphor 06' },
        title: { ru: 'волна и тайминг', en: 'the timing wave' },
        desc: {
          ru: <>IFS + AI — это момент, когда достаточно зрелости в психологии и достаточно мощности в моделях.</>,
          en: <>IFS + AI is a timing moment: psychology maturity meets model capability.</>,
        },
        Visual: asVisual(WaveVisual),
      },
      {
        id: 'stack',
        label: { ru: 'metaphor 07', en: 'metaphor 07' },
        title: { ru: 'модульный стек', en: 'modular stack' },
        desc: {
          ru: (
            <>
              запись → обработка → база знаний. важно, чтобы стек не ломал внимание — а поддерживал <span className="text-text">ритм</span>
              .
            </>
          ),
          en: (
            <>
              capture → processing → knowledge base. the stack should protect attention and support the{' '}
              <span className="text-text">rhythm</span>.
            </>
          ),
        },
        Visual: asVisual(StackVisual),
      },
      {
        id: 'agency',
        label: { ru: 'metaphor 08', en: 'metaphor 08' },
        title: { ru: 'спектр агентности', en: 'agency spectrum' },
        desc: {
          ru: <>агент может быть «тихим» и реактивным — или прерывать и защищать от ошибки. в коучинге важна дозировка.</>,
          en: <>an agent can be quiet/reactive — or interrupt to prevent mistakes. in coaching, dosage matters.</>,
        },
        Visual: AgencyVisual,
      },
      {
        id: 'ecosystem',
        label: { ru: 'metaphor 09', en: 'metaphor 09' },
        title: { ru: 'экосистема практики', en: 'practice ecosystem' },
        desc: {
          ru: <>не один чат — а цикл: контакт → подготовка → сессия → пост-обработка → возвращение.</>,
          en: <>not one chat — but a cycle: contact → pre-work → session → post-processing → re-engage.</>,
        },
        Visual: asVisual(EcosystemVisual),
      },
      {
        id: 'dogfooding',
        label: { ru: 'metaphor 10', en: 'metaphor 10' },
        title: { ru: 'dogfooding', en: 'dogfooding' },
        desc: {
          ru: <>делать на себе — прежде чем масштабировать. практика создаёт доверие к инструменту.</>,
          en: <>build for yourself before scaling. practice creates trust in the tool.</>,
        },
        Visual: asVisual(DogfoodingVisual),
      },
    ],
    []
  );

  return (
    <section id="metaphors" className="border-b border-border py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-dim">
            <T ru="visual metaphors" en="visual metaphors" />
          </div>
          <h2 className="mt-3 font-serif text-3xl font-light italic text-text md:text-4xl">
            <T ru="интерфейс для сложных смыслов" en="an interface for complex meanings" />
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-muted">
            <T
              ru="эти метафоры – мост между психологией, технологиями и практикой. они сделаны как интерактивные «схемы мышления»."
              en="these metaphors bridge psychology, technology, and practice. built as interactive “thinking diagrams”."
            />
          </p>
        </div>

        <div className="grid gap-8">
          {items.map((item) => (
            <MetaphorRow key={item.id} item={item} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}

function MetaphorRow({ item, lang }: { item: MetaphorItem; lang: 'ru' | 'en' }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, { amount: 0.35, once: false });

  return (
    <motion.div
      ref={ref}
      id={item.id}
      className="overflow-hidden rounded-xl border border-border bg-bg/30 backdrop-blur"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="grid gap-6 p-6 md:grid-cols-[1fr_1.05fr] md:gap-10 md:p-10">
        <div>
          <div className="inline-flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-dim">{lang === 'ru' ? item.label.ru : item.label.en}</span>
            <span className="h-px w-10 bg-border" />
          </div>
          <h3 className="mt-4 font-serif text-2xl font-light italic text-text">{lang === 'ru' ? item.title.ru : item.title.en}</h3>
          <p className="mt-4 text-sm leading-7 text-muted">{lang === 'ru' ? item.desc.ru : item.desc.en}</p>
        </div>

        <div className="rounded-lg border border-border bg-card/60 p-4 md:p-6">
          <item.Visual isActive={inView} />
        </div>
      </div>
    </motion.div>
  );
}


